/**
 * Admin Diagnostic — V2.N.9
 *
 * Page de debug : état des tables critiques (existence, count,
 * dernière entrée). Permet de répondre à « pourquoi rien ne remonte ? ».
 *
 * Bouton « Forcer sync local→DB » pour pousser immédiatement les
 * données de l'admin courant (utile en post-déploiement).
 */

import { useEffect, useState } from 'react';
import { fetchAdminAction } from '../lib/fetchAdminAction';
import { adminPageStyle, adminCardStyle, adminTableStyle, adminThStyle, adminTdStyle, adminCodeStyle } from '../lib/adminStyles';
import { relTime } from '../lib/dateHelpers';
import { syncDeviceNow } from '../../services/deviceSyncService';
import '../layout/AdminLayout.css';

interface TableHealth {
  table: string;
  exists: boolean;
  count: number | null;
  last_record_at: string | null;
  error: string | null;
}

interface Diagnostic {
  auth_mode: string;
  auth_user: string | null;
  tables: TableHealth[];
  env: {
    supabase_url_configured: boolean;
    service_role_configured: boolean;
    admin_key_configured: boolean;
  };
}

export function Diagnostic() {
  const [data, setData] = useState<Diagnostic | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const d = await fetchAdminAction<Diagnostic>('/api/admin/diagnostic', 'diagnostic');
    setData(d);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSync = () => {
    setSyncing(true);
    setSyncMsg('Sync en cours…');
    syncDeviceNow();
    // Le sync est fire-and-forget. On donne 2s puis on recharge le diag.
    setTimeout(async () => {
      await load();
      setSyncMsg('Sync envoyé. Rechargez si rien ne change après quelques secondes.');
      setSyncing(false);
    }, 2000);
  };

  return (
    <main style={adminPageStyle}>
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Diagnostic</h1>
        <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
          État des tables critiques + outils de debug.
        </div>
      </header>

      {/* Auth & env */}
      <section style={{ ...adminCardStyle, marginBottom: '16px' }}>
        <h2 style={h2Style}>Auth & env</h2>
        {loading ? <Loading /> : !data ? <Empty hint="Diag indisponible" /> : (
          <table style={adminTableStyle}>
            <tbody>
              <Row label="Auth mode" value={data.auth_mode} />
              <Row label="Auth user" value={data.auth_user || '—'} />
              <Row label="SUPABASE_URL" value={data.env.supabase_url_configured ? '✓ configurée' : '✗ MANQUANTE'} ok={data.env.supabase_url_configured} />
              <Row label="SERVICE_ROLE_KEY" value={data.env.service_role_configured ? '✓ configurée' : '✗ MANQUANTE'} ok={data.env.service_role_configured} />
              <Row label="ADMIN_KEY (legacy)" value={data.env.admin_key_configured ? '✓ configurée' : '— (optionnel)'} />
            </tbody>
          </table>
        )}
      </section>

      {/* Tables */}
      <section style={{ ...adminCardStyle, marginBottom: '16px' }}>
        <h2 style={h2Style}>Tables</h2>
        {loading ? <Loading /> : !data ? <Empty hint="—" /> : (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminThStyle}>Table</th>
                <th style={adminThStyle}>État</th>
                <th style={adminThStyle}>Count</th>
                <th style={adminThStyle}>Dernière entrée</th>
                <th style={adminThStyle}>Erreur</th>
              </tr>
            </thead>
            <tbody>
              {data.tables.map((t) => (
                <tr key={t.table}>
                  <td style={{ ...adminTdStyle, fontWeight: 600 }}>{t.table}</td>
                  <td style={adminTdStyle}>{t.exists ? <span style={{ color: '#0e9f6e' }}>✓ OK</span> : <span style={{ color: '#df1b41' }}>✗ ABSENTE</span>}</td>
                  <td style={adminTdStyle}>{t.count ?? '—'}</td>
                  <td style={adminTdStyle}>{t.last_record_at ? relTime(t.last_record_at) : '—'}</td>
                  <td style={{ ...adminTdStyle, color: '#df1b41', fontSize: '11px' }}>
                    {t.error ? <code style={adminCodeStyle}>{t.error.slice(0, 80)}</code> : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Action: force sync */}
      <section style={adminCardStyle}>
        <h2 style={h2Style}>Forcer un sync local → DB</h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '10px' }}>
          Pousse immédiatement TES données locales (wallets monitors, portfolios, pages activées) vers la base. Utile si tu viens de déployer et veux vérifier que la chaîne fonctionne.
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{ padding: '9px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: syncing ? 'wait' : 'pointer', opacity: syncing ? 0.6 : 1 }}
        >
          {syncing ? 'Sync…' : 'Forcer sync maintenant'}
        </button>
        {syncMsg && <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '10px' }}>{syncMsg}</div>}
      </section>
    </main>
  );
}

function Row({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <tr>
      <td style={{ ...adminTdStyle, color: 'var(--muted)', width: '40%' }}>{label}</td>
      <td style={{ ...adminTdStyle, color: ok === false ? '#df1b41' : 'var(--text)' }}>{value}</td>
    </tr>
  );
}
function Loading() { return <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>; }
function Empty({ hint }: { hint: string }) { return <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>{hint}</div>; }
const h2Style: React.CSSProperties = { fontSize: '15px', fontWeight: 700, margin: 0, marginBottom: '12px' };
